import {
  useEffect,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
} from 'react'
import * as THREE from 'three'
import { useAppStore } from '@core/store'
import type {
  SectionLocalExtents,
  SectionOrientation,
} from '@viewer/store/sectionSlice'
import { viewerApi } from '@viewer/lib/viewerApi'
import {
  FACE_KEYS,
  localFaceCenter,
  localFaceNormal,
  localToWorldDir,
  localToWorldPoint,
  obbCorners,
  type FaceKey,
} from './sectionMath'
import {
  type V3,
  arestaNoEcra,
  poligonoNoEcra,
  pontoNoEcra,
} from './desenhoDeCorte'
import { camaraDoViewer } from './camaraDoViewer'

// As seis faces pelos índices dos cantos de obbCorners (bits zi*4 +
// yi*2 + xi), cada uma por ordem à volta do polígono.
const FACES: Array<[number, number, number, number]> = [
  [0, 2, 6, 4],
  [1, 3, 7, 5],
  [0, 1, 5, 4],
  [2, 3, 7, 6],
  [0, 1, 3, 2],
  [4, 5, 7, 6],
]

// 12 edges of an axis-aligned box for the corner-order produced by
// obbCorners (z-major, then y, then x): bits encode (zi*4 + yi*2 + xi).
const EDGES: Array<[number, number]> = [
  // bottom face (z=min)
  [0, 1],
  [1, 3],
  [3, 2],
  [2, 0],
  // top face (z=max)
  [4, 5],
  [5, 7],
  [7, 6],
  [6, 4],
  // verticals (z direction)
  [0, 4],
  [1, 5],
  [2, 6],
  [3, 7],
]

type Vec2 = { x: number; y: number }

function projectToScreen(
  p: THREE.Vector3 | { x: number; y: number; z: number },
  camera: THREE.PerspectiveCamera,
  w: number,
  h: number,
): Vec2 | null {
  const v = new THREE.Vector3(p.x, p.y, p.z)
  const camSpace = v.clone().applyMatrix4(camera.matrixWorldInverse)
  if (camSpace.z >= -1e-3) return null
  v.project(camera)
  return {
    x: ((v.x + 1) / 2) * w,
    y: ((1 - v.y) / 2) * h,
  }
}

interface DragState {
  pointerId: number
  faceKey: FaceKey
  axisIdx: 0 | 1 | 2 // which OBB axis this face belongs to
  isMax: boolean
  startMouse: Vec2
  startExtent: number
  oppositeExtent: number
  axisVecScreen: Vec2
  axisVecLenSq: number
  canvas: HTMLCanvasElement
}

function SectionBoxOverlay() {
  const enabled = useAppStore((s) => s.sectionEnabled)
  const orientation = useAppStore((s) => s.sectionOrientation)
  const extents = useAppStore((s) => s.sectionExtents)
  const alignMode = useAppStore((s) => s.sectionAlignMode)
  const mostrarCaixa = useAppStore((s) => s.sectionMostrarCaixa)
  const setExtents = useAppStore((s) => s.setSectionExtents)
  const [, force] = useState(0)
  const fingerprintRef = useRef('')
  const dragRef = useRef<DragState | null>(null)
  const cameraSavedRef = useRef(false)

  // rAF loop: re-render when camera moves. Com a caixa escondida não
  // há nada para redesenhar.
  useEffect(() => {
    if (!enabled || !mostrarCaixa) return
    let raf = 0
    let cancelled = false
    const tick = () => {
      if (cancelled) return
      const world = viewerApi.getWorld()
      if (world) {
        const m = world.camera.three.matrixWorldInverse.elements
        const fp = `${m[0].toFixed(4)}|${m[5].toFixed(4)}|${m[12].toFixed(2)}|${m[13].toFixed(2)}|${m[14].toFixed(2)}|${(world.renderer!.three.domElement.clientWidth | 0)}x${(world.renderer!.three.domElement.clientHeight | 0)}`
        if (fp !== fingerprintRef.current) {
          fingerprintRef.current = fp
          force((t) => (t + 1) & 0xffff)
        }
      }
      raf = requestAnimationFrame(tick)
    }
    raf = requestAnimationFrame(tick)
    return () => {
      cancelled = true
      cancelAnimationFrame(raf)
    }
  }, [enabled, mostrarCaixa])

  if (!enabled || !orientation || !extents) return null
  // Caixa escondida: o corte continua (é o useSectionBox que o aplica),
  // mas não se desenha nada nem há pegas a apanhar cliques. Fica só o
  // aviso do "Alinhar à face", que é uma instrução e não a caixa.
  if (!mostrarCaixa) return alignMode ? <AvisoAlinhar /> : null
  const world = viewerApi.getWorld()
  if (!world) return null
  const canvas = world.renderer!.three.domElement
  const w = canvas.clientWidth
  const h = canvas.clientHeight
  const camera = world.camera.three as THREE.PerspectiveCamera

  // Os cantos no mundo, e o desenho recortado contra a câmara. Antes,
  // um só canto atrás da câmara escondia a caixa toda e as pegas; com a
  // câmara dentro do edifício, que é onde se trabalha com uma caixa de
  // corte, ela nunca se via.
  const corners: V3[] = obbCorners(orientation, extents).map((c) => [c.x, c.y, c.z] as V3)
  const cam = camaraDoViewer()
  const faces = cam ? FACES.map((f) => poligonoNoEcra(cam, f.map((i) => corners[i]))) : []
  const arestas = cam
    ? EDGES.map(([a, b]) => arestaNoEcra(cam, corners[a], corners[b])).filter(
        (x): x is NonNullable<typeof x> => x !== null,
      )
    : []

  const onArrowPointerDown = (
    event: ReactPointerEvent<HTMLButtonElement>,
    face: FaceKey,
  ) => {
    event.preventDefault()
    event.stopPropagation()
    const cur = useAppStore.getState()
    if (!cur.sectionExtents || !cur.sectionOrientation) return
    const o = cur.sectionOrientation
    const e = cur.sectionExtents
    const wld = viewerApi.getWorld()
    if (!wld) return
    const cnv = wld.renderer!.three.domElement
    const cam = wld.camera.three as THREE.PerspectiveCamera
    const cw = cnv.clientWidth
    const ch = cnv.clientHeight

    const center = localToWorldPoint(o, localFaceCenter(e, face))
    const outward = localToWorldDir(o, localFaceNormal(face)).normalize()
    const p0 = projectToScreen(center, cam, cw, ch)
    const p1 = projectToScreen(
      {
        x: center.x + outward.x,
        y: center.y + outward.y,
        z: center.z + outward.z,
      },
      cam,
      cw,
      ch,
    )
    if (!p0 || !p1) return
    const axisVecScreen: Vec2 = { x: p1.x - p0.x, y: p1.y - p0.y }
    const axisVecLenSq =
      axisVecScreen.x * axisVecScreen.x + axisVecScreen.y * axisVecScreen.y
    if (axisVecLenSq < 0.01) return
    const rect = cnv.getBoundingClientRect()
    const startMouse: Vec2 = {
      x: event.clientX - rect.left,
      y: event.clientY - rect.top,
    }
    const axisIdx: 0 | 1 | 2 =
      face[3] === 'X' ? 0 : face[3] === 'Y' ? 1 : 2
    const isMax = face.startsWith('max')
    const startExtent = e[face]
    const oppositeKey = (
      isMax ? `min${face[3]}` : `max${face[3]}`
    ) as keyof SectionLocalExtents
    dragRef.current = {
      pointerId: event.pointerId,
      faceKey: face,
      axisIdx,
      isMax,
      startMouse,
      startExtent,
      oppositeExtent: e[oppositeKey],
      axisVecScreen,
      axisVecLenSq,
      canvas: cnv,
    }
    if (!cameraSavedRef.current) {
      wld.camera.controls.enabled = false
      cameraSavedRef.current = true
    }
    document.addEventListener('pointermove', onDocPointerMove)
    document.addEventListener('pointerup', onDocPointerUp)
    document.addEventListener('pointercancel', onDocPointerUp)
  }

  const onDocPointerMove = (event: globalThis.PointerEvent) => {
    const drag = dragRef.current
    if (!drag || event.pointerId !== drag.pointerId) return
    const rect = drag.canvas.getBoundingClientRect()
    const dx = event.clientX - rect.left - drag.startMouse.x
    const dy = event.clientY - rect.top - drag.startMouse.y
    const t =
      (dx * drag.axisVecScreen.x + dy * drag.axisVecScreen.y) /
      drag.axisVecLenSq
    const sign = drag.isMax ? 1 : -1
    const newExtent = drag.startExtent + sign * t
    const cur = useAppStore.getState().sectionExtents
    if (!cur) return
    const next: SectionLocalExtents = { ...cur }
    if (drag.isMax) {
      next[drag.faceKey] = Math.max(newExtent, drag.oppositeExtent)
    } else {
      next[drag.faceKey] = Math.min(newExtent, drag.oppositeExtent)
    }
    setExtents(next)
  }

  const onDocPointerUp = (event: globalThis.PointerEvent) => {
    const drag = dragRef.current
    if (!drag || event.pointerId !== drag.pointerId) return
    dragRef.current = null
    if (cameraSavedRef.current) {
      const wld = viewerApi.getWorld()
      if (wld) wld.camera.controls.enabled = true
      cameraSavedRef.current = false
    }
    document.removeEventListener('pointermove', onDocPointerMove)
    document.removeEventListener('pointerup', onDocPointerUp)
    document.removeEventListener('pointercancel', onDocPointerUp)
  }

  return (
    // overflow-hidden: keep the dashed edges and the face arrows
    // contained inside the canvas viewport so they don't leak over
    // the sidebar / topbar / right panels when the user zooms close
    // and a face center projects past the canvas boundary.
    <div className="pointer-events-none absolute inset-0 z-10 overflow-hidden">
      <svg
        className="absolute inset-0 h-full w-full"
        width={w}
        height={h}
      >
        {/* As faces, quase transparentes: é o que faz a caixa ler-se como
            um volume e não como o contorno do modelo. */}
        {faces.map((pts, i) =>
          pts.length >= 3 ? (
            <polygon
              key={`f${i}`}
              points={pts.map((q) => `${q.x},${q.y}`).join(' ')}
              fill="var(--color-accent)"
              fillOpacity={0.05}
              stroke="none"
            />
          ) : null,
        )}
        {arestas.map(([pa, pb], i) => (
          <line
            key={i}
            x1={pa.x}
            y1={pa.y}
            x2={pb.x}
            y2={pb.y}
            stroke="var(--color-accent)"
            strokeWidth={2}
            strokeOpacity={0.9}
          />
        ))}
      </svg>
      {/* Hide arrows in alignMode so the user can click through to
          pick a face. */}
      {!alignMode &&
        FACE_KEYS.map((face) => {
          const center = localToWorldPoint(
            orientation,
            localFaceCenter(extents, face),
          )
          const outward = localToWorldDir(
            orientation,
            localFaceNormal(face),
          ).normalize()
          if (cam && !pontoNoEcra(cam, [center.x, center.y, center.z])) return null
          const projected = projectToScreen(center, camera, w, h)
          const tip = projectToScreen(
            {
              x: center.x + outward.x,
              y: center.y + outward.y,
              z: center.z + outward.z,
            },
            camera,
            w,
            h,
          )
          if (!projected || !tip) return null
          const sdir: Vec2 = {
            x: tip.x - projected.x,
            y: tip.y - projected.y,
          }
          const slen = Math.hypot(sdir.x, sdir.y)
          if (slen < 0.5) return null
          const angleDeg = (Math.atan2(sdir.y, sdir.x) * 180) / Math.PI
          // Pegas discretas: mais pequenas e meio transparentes até se
          // passar por cima. As grandes (32 px, opacas) tapavam a vista
          // no meio das faces, que é onde se está a olhar.
          const offset = 16
          const ux = sdir.x / slen
          const uy = sdir.y / slen
          const cx = projected.x + ux * offset
          const cy = projected.y + uy * offset
          // Don't render arrows that fall outside the canvas: they'd
          // visually escape over the surrounding UI even though
          // overflow-hidden clips them, and they'd also intercept
          // pointer events in dead zones of the scene. A 12px bleed
          // keeps partially-visible arrows still hoverable when the
          // face is right at the edge.
          const BLEED = 12
          if (
            cx < -BLEED ||
            cy < -BLEED ||
            cx > w + BLEED ||
            cy > h + BLEED
          ) {
            return null
          }
          return (
            <button
              key={face}
              type="button"
              data-testid="section-box-pega"
              aria-label={`Pega da face ${face}`}
              onPointerDown={(e) => onArrowPointerDown(e, face)}
              className="pointer-events-auto absolute flex h-6 w-6 items-center justify-center rounded-full border border-[var(--color-accent)] bg-[var(--color-surface)]/70 text-[var(--color-accent)] opacity-75 shadow hover:bg-[var(--color-accent)] hover:text-[var(--color-bg)] hover:opacity-100"
              style={{
                left: cx,
                top: cy,
                transform: `translate(-50%, -50%) rotate(${angleDeg}deg)`,
                cursor: 'grab',
              }}
              title={`Arrastar para mover a face ${face}`}
            >
              <svg viewBox="0 0 12 12" width="11" height="11" aria-hidden>
                <path
                  d="M3 1.5 L8.5 6 L3 10.5"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={1.8}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          )
        })}
      {alignMode && <AvisoAlinhar dentro />}
    </div>
  )
}

/** O aviso do modo "Alinhar à face". `dentro`: já está no contentor do
 *  desenho; sem ele (caixa escondida) traz o seu próprio contentor. */
function AvisoAlinhar({ dentro = false }: { dentro?: boolean }) {
  const aviso = (
    <div className="pointer-events-none absolute inset-x-0 top-12 mx-auto w-fit rounded-md border border-[var(--color-accent)] bg-[var(--color-surface)]/95 px-3 py-1 text-xs text-[var(--color-accent)] shadow">
      Alinhar à face: clica numa face do modelo · Esc para cancelar
    </div>
  )
  if (dentro) return aviso
  return (
    <div className="pointer-events-none absolute inset-0 z-10 overflow-hidden">{aviso}</div>
  )
}

// Remove unused imports if any.
type _UseOrient = SectionOrientation
type _UseExtents = SectionLocalExtents
void undefined as unknown as _UseOrient
void undefined as unknown as _UseExtents

export default SectionBoxOverlay
